import React, { useEffect, useRef, useState } from 'react';
import globalImageCache from './globalImageCache';
import '../../styles/contents/shortFormQuiz/ShortFormQuizEraseAndPick.css';
const coverImageWebp = '/assets/images/contents/shortForm/erase/Cover.webp';
const gem1 = '/assets/images/contents/shortForm/erase/Gem1.png';
const gem2 = '/assets/images/contents/shortForm/erase/Gem2.png';
const junk2 = '/assets/images/contents/shortForm/erase/junk2.png';
const junk3 = '/assets/images/contents/shortForm/erase/junk3.png';
const stone = '/assets/images/contents/shortForm/erase/Stone.png';
const bubbleSuccessUrl = '/assets/images/contents/shortForm/bubble/Bubble_Success.png';
const bubbleFailUrl = '/assets/images/contents/shortForm/bubble/Bubble_Fail.png';

const ShortFormQuizEraseAndPick = ({
  stagePhase,
  localStage,
  quiz,
  setStagePhase,
  correctCountRef,
  answersRef,
  wrongIndexesRef,
  stageAnsweredRef,
  feedbackActive,
  feedbackIsSuccess,
  feedbackRect,
  setFeedbackActive,
  setFeedbackIsSuccess,
  setFeedbackRect,
  setIsGameInProgress,
  canvasRef,
  // 새로 추가된 props
  includeTutorial,
  isTutorialStage
}) => {
  const [items, setItems] = useState([]);
  const [isErasing, setIsErasing] = useState(false);
  const [animatingItem, setAnimatingItem] = useState(null);
  const [animationStartTime, setAnimationStartTime] = useState(null);
  const [shakingItem, setShakingItem] = useState(null);
  const [shakeStartTime, setShakeStartTime] = useState(null);
  const lastPoint = useRef(null);
  const imagesRef = useRef({ cover: null, gems: [], success: null, fail: null });

  const [imagesReady, setImagesReady] = useState(false);
  
  const maskCanvasRef = useRef(null);
  const rafRef = useRef(null);
  const topUiRatio = 0.22;

  // 전역 캐시에서 이미지 가져오기
  useEffect(() => {
    if (globalImageCache.isReadyToUse()) {
      // 전역 캐시에서 이미지 가져오기
      const cover = globalImageCache.get(coverImageWebp);
      const gemImg1 = globalImageCache.get(gem1);
      const gemImg2 = globalImageCache.get(gem2);
      const junkImg2 = globalImageCache.get(junk2);
      const junkImg3 = globalImageCache.get(junk3);
      const stoneImg = globalImageCache.get(stone);
      const success = globalImageCache.get(bubbleSuccessUrl);
      const fail = globalImageCache.get(bubbleFailUrl);
      
      // imagesRef에 올바른 형식으로 할당
      imagesRef.current = { 
        cover: cover,
        success: success,
        fail: fail,
        gems: [gemImg1, gemImg2, junkImg2, junkImg3, stoneImg]
      };
      
      setImagesReady(true);
    } else {
      // 전역 캐시가 준비되지 않았으면 기존 방식으로 로딩
      
      // 이미지 객체들 직접 생성 (fallback)
      const cover = new Image(); cover.src = coverImageWebp;
      const gemImg1 = new Image(); gemImg1.src = gem1;
      const gemImg2 = new Image(); gemImg2.src = gem2;
      const junkImg2 = new Image(); junkImg2.src = junk2;
      const junkImg3 = new Image(); junkImg3.src = junk3;
      const stoneImg = new Image(); stoneImg.src = stone;
      const success = new Image(); success.src = bubbleSuccessUrl;
      const fail = new Image(); fail.src = bubbleFailUrl;

      let loaded = 0;
      const totalImages = 8;
      
      const onLoaded = () => { 
        loaded += 1; 
        if (loaded >= totalImages) { 
          setImagesReady(true);
        } 
      };
      
      const onErr = (src) => { 
        console.error(`지우고 뽑기 이미지 로딩 실패: ${src}`); 
        onLoaded(); // 에러도 완료로 카운트
      };
      
      [cover, gemImg1, gemImg2, junkImg2, junkImg3, stoneImg, success, fail].forEach((img) => { 
        img.onload = onLoaded; 
        img.onerror = () => onErr(img.src); 
      });
      
      // imagesRef에 올바른 형식으로 할당
      imagesRef.current = { 
        cover: cover,
        success: success,
        fail: fail,
        gems: [gemImg1, gemImg2, junkImg2, junkImg3, stoneImg]
      };

      // 클린업
      return () => {
        [cover, gemImg1, gemImg2, junkImg2, junkImg3, stoneImg, success, fail].forEach((img) => { 
          img.onload = null; 
          img.onerror = null; 
        });
      };
    }
  }, [globalImageCache]);

  useEffect(() => {
    if (stagePhase !== 'play' || !canvasRef.current || !imagesReady) return;
    
    const canvas = canvasRef.current;
    const parent = canvas.parentElement;
    const width = parent?.clientWidth;
    const height = parent?.clientHeight;

    if (!width || !height) return;
    canvas.width = width;
    canvas.height = height;

    maskCanvasRef.current = document.createElement('canvas');
    maskCanvasRef.current.width = width;
    maskCanvasRef.current.height = height;
    const maskCtx = maskCanvasRef.current.getContext('2d');
    maskCtx.drawImage(imagesRef.current.cover, 0, height * topUiRatio, width, height * (1 - topUiRatio));

    const gameAreaTop = height * topUiRatio;
    const gameAreaHeight = height * (1 - topUiRatio);

    let choices = [];
    if (isTutorialStage) {
      // 튜토리얼에서는 아이템 1개만 생성
      choices = ['정답'];
    } else {
      // 퀴즈 단계 - 수정된 인덱스 계산
      let qIndex;
      if (includeTutorial) {
        qIndex = localStage - 5; // localStage 5 = qIndex 0
      } else {
        qIndex = localStage - 1; // localStage 1 = qIndex 0
      }
      
      const problem = quiz?.questions?.[qIndex];
      if (problem && Array.isArray(problem.options)) {
        choices = problem.options;
      } else {
        choices = ['선택지 A', '선택지 B', '선택지 C', '선택지 D'];
      }
    }

    // 아이템들이 겹치지 않게 배치하는 함수
    const generateNonOverlappingPositions = (count) => {
      const positions = [];
      const itemSize = Math.min(width, gameAreaHeight) * 0.18;
      const minDistance = itemSize * 1.2; // 아이템 크기의 1.2배 거리 유지
      const maxAttempts = 100; // 최대 시도 횟수
      
      for (let i = 0; i < count; i++) {
        let attempts = 0;
        let validPosition = false;
        let newPos;
        
        while (!validPosition && attempts < maxAttempts) {
          newPos = {
            x: width * (0.15 + Math.random() * 0.7),
            y: gameAreaTop + (gameAreaHeight * (0.15 + Math.random() * 0.7))
          };
          
          // 다른 아이템들과의 거리 확인
          validPosition = positions.every(pos => {
            const distance = Math.hypot(newPos.x - pos.x, newPos.y - pos.y);
            return distance >= minDistance;
          });
          
          attempts++;
        }
        
        // 적절한 위치를 찾지 못했다면 그리드 방식으로 배치
        if (!validPosition) {
          const cols = Math.ceil(Math.sqrt(count));
          const rows = Math.ceil(count / cols);
          const col = i % cols;
          const row = Math.floor(i / cols);
          
          newPos = {
            x: width * (0.2 + (col * 0.6) / (cols - 1 || 1)),
            y: gameAreaTop + gameAreaHeight * (0.2 + (row * 0.6) / (rows - 1 || 1))
          };
        }
        
        positions.push(newPos);
      }
      
      return positions;
    };

    // 이미지 인덱스를 랜덤하게 섞기
    const imageIndices = Array.from({ length: choices.length }, (_, i) => i % 5);
    
    // Fisher-Yates 셔플 알고리즘으로 이미지 인덱스 섞기
    for (let i = imageIndices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [imageIndices[i], imageIndices[j]] = [imageIndices[j], imageIndices[i]];
    }

    const positions = generateNonOverlappingPositions(choices.length);
    const newItems = choices.map((choice, index) => ({
      id: index,
      text: choice,
      image: imagesRef.current.gems[imageIndices[index]], // 랜덤하게 섞인 이미지 인덱스 사용
      x: positions[index].x,
      y: positions[index].y,
      size: Math.min(width, gameAreaHeight) * 0.18,
    }));
    setItems(newItems);

  }, [stagePhase, imagesReady, localStage, quiz, canvasRef, isTutorialStage, includeTutorial]);

  const getCanvasPoint = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX || e.touches[0].clientX;
    const clientY = e.clientY || e.touches[0].clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const handleSelectAnswer = (selectedIndex) => {
    if (stageAnsweredRef.current) return;
    stageAnsweredRef.current = true;
    
    // 아이템 선택 시 즉시 타이머 정지 및 피드백 단계로 전환
    setIsGameInProgress(false);
    setStagePhase('feedback'); // 즉시 피드백 단계로 전환하여 타이머 멈춤
    
    // 수정된 인덱스 계산
    let questionIndex;
    if (includeTutorial) {
      questionIndex = localStage - 5; // localStage 5 = qIndex 0
    } else {
      questionIndex = localStage - 1; // localStage 1 = qIndex 0
    }
    
    let isCorrect = isTutorialStage; // 튜토리얼은 항상 성공

    if (!isTutorialStage) {
      const currentProblem = quiz?.questions?.[questionIndex];
      isCorrect = selectedIndex === currentProblem?.answerIndex;
      if (isCorrect) {
        correctCountRef.current += 1;
      } else {
        if (questionIndex >= 0) {
          wrongIndexesRef.current.push(questionIndex + 1);
        }
      }
      
      if (questionIndex >= 0 && Array.isArray(answersRef.current)) {
        answersRef.current[questionIndex] = selectedIndex;
      }
    }
    
    // 즉시 피드백 설정
    setFeedbackIsSuccess(isCorrect);
    const { width, height } = canvasRef.current;
    setFeedbackRect({ x: width / 2, y: height / 2, w: Math.min(width, height) * 0.5, h: Math.min(width, height) * 0.5 });
    setFeedbackActive(true);
    
    // 선택된 아이템을 찾아서 애니메이션 설정
    const selectedItem = items.find(item => item.id === selectedIndex);
    if (selectedItem && isCorrect) {
      // 성공 시 애니메이션 시작 (백그라운드에서 실행)
      setAnimatingItem({
        ...selectedItem,
        originalX: selectedItem.x,
        originalY: selectedItem.y,
        originalSize: selectedItem.size,
        rotation: 0,
        scale: 1,
        zOffset: 0
      });
      setAnimationStartTime(Date.now());
    } else if (selectedItem && !isCorrect) {
      // 실패 시 shake 애니메이션 시작
      setShakingItem({
        ...selectedItem,
        originalX: selectedItem.x,
        originalY: selectedItem.y
      });
      setShakeStartTime(Date.now());
      
      // 0.6초 후에 shake 애니메이션 종료
      setTimeout(() => {
        setShakingItem(null);
        setShakeStartTime(null);
      }, 600);
    }
  };

  useEffect(() => {
    if ((stagePhase !== 'play' && stagePhase !== 'feedback') || !canvasRef.current || !imagesReady) {
      if(rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }
    
    // play 상태에서는 items가 필요하지만, feedback 상태에서는 필요없음
    if (stagePhase === 'play' && items.length === 0) {
      if(rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // 전체 화면에 배경색 적용 (질문 텍스트 뒤까지)
      ctx.fillStyle = '#3C261F';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // 일반 아이템들 그리기 (마스크보다 뒤에 위치)
      items.forEach(item => {
        // 애니메이션 중인 아이템이나 흔들리는 아이템은 건너뛰기
        if (animatingItem && animatingItem.id === item.id) return;
        if (shakingItem && shakingItem.id === item.id) return;
        
        ctx.drawImage(item.image, item.x - item.size / 2, item.y - item.size / 2, item.size, item.size);
        ctx.fillStyle = 'white';
        ctx.font = `bold ${item.size * 0.18}px Pretendard`;
        ctx.textAlign = 'center';
        ctx.fillText(item.text, item.x, item.y + item.size * 0.6);
      });
      
      // 흔들리는 아이템 그리기 (마스크보다 뒤에 위치)
      if (shakingItem && shakeStartTime) {
        const elapsed = Date.now() - shakeStartTime;
        const duration = 600; // 0.6초 애니메이션
        const progress = Math.min(elapsed / duration, 1);
        
        // 좌우 흔들림 효과 (사인파 사용, 진폭은 점점 줄어듦)
        const frequency = 20; // 흔들림 빈도
        const amplitude = 15 * (1 - progress); // 시간이 지나면서 진폭 감소
        const shakeX = Math.sin(elapsed * frequency * Math.PI / 1000) * amplitude;
        
        const itemX = shakingItem.originalX + shakeX;
        const itemY = shakingItem.originalY;
        
        // 틀린 답을 강조하기 위해 약간 붉은 tint 추가
        ctx.save();
        ctx.filter = 'hue-rotate(30deg) saturate(1.5)';
        ctx.drawImage(shakingItem.image, itemX - shakingItem.size / 2, itemY - shakingItem.size / 2, shakingItem.size, shakingItem.size);
        ctx.restore();
        
        ctx.fillStyle = 'white';
        ctx.font = `bold ${shakingItem.size * 0.18}px Pretendard`;
        ctx.textAlign = 'center';
        ctx.fillText(shakingItem.text, itemX, itemY + shakingItem.size * 0.6);
      }
      
      // 마스크 그리기 (일반 아이템들과 흔들리는 아이템 위에, 애니메이션 아이템 아래에 위치)
      if (maskCanvasRef.current) {
        ctx.drawImage(maskCanvasRef.current, 0, 0);
      }
      
      // 애니메이션 아이템 그리기 (마스크보다 앞에 위치 - 선택했을 때만)
      if (animatingItem && animationStartTime) {
        const elapsed = Date.now() - animationStartTime;
        const duration = 1500; // 1.5초 애니메이션
        const progress = Math.min(elapsed / duration, 1);
        
        // 이지-아웃 애니메이션 함수
        const easeOut = (t) => 1 - Math.pow(1 - t, 3);
        const animProgress = easeOut(progress);
        
        // 회전 (한 바퀴 = 360도)
        const rotation = animProgress * Math.PI * 2;
        
        // 스케일 (1.5배까지 커짐)
        const scale = 1 + animProgress * 0.5;
        
        // Z축 이동 (앞으로 튀어나오는 효과)
        const zOffset = animProgress * 50;
        
        // 화면 정가운데로 이동하는 애니메이션
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        const itemX = animatingItem.originalX + (centerX - animatingItem.originalX) * animProgress;
        const itemY = animatingItem.originalY + (centerY - animatingItem.originalY) * animProgress - zOffset;
        const itemSize = animatingItem.originalSize * scale;
        
        ctx.save();
        ctx.translate(itemX, itemY);
        ctx.rotate(rotation);
        
        // 그림자 효과 (Z축 이동에 따른)
        if (zOffset > 0) {
          ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
          ctx.shadowBlur = zOffset * 0.5;
          ctx.shadowOffsetX = zOffset * 0.3;
          ctx.shadowOffsetY = zOffset * 0.3;
        }
        
        ctx.drawImage(
          animatingItem.image, 
          -itemSize / 2, 
          -itemSize / 2, 
          itemSize, 
          itemSize
        );
        
        ctx.restore();
        
        // 텍스트는 회전하지 않고 표시
        ctx.fillStyle = 'white';
        ctx.font = `bold ${itemSize * 0.18}px Pretendard`;
        ctx.textAlign = 'center';
        ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
        ctx.shadowBlur = 5;
        ctx.fillText(animatingItem.text, itemX, itemY + itemSize * 0.6);
        ctx.shadowBlur = 0;
        
        // 애니메이션 완료 시 정리
        if (progress >= 1) {
          setAnimatingItem(null);
          setAnimationStartTime(null);
        }
      }

      // 피드백 오버레이 그리기 (BubbleWrap 방식 참고)
      if (stagePhase === 'feedback' && feedbackActive && imagesRef.current) {
        ctx.fillStyle = 'rgba(107, 114, 128, 0.5)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        const img = feedbackIsSuccess ? imagesRef.current.success : imagesRef.current.fail;
        if (img && img.complete) {
          const size = Math.min(canvas.width, canvas.height) * 0.3;
          const cx = canvas.width / 2;
          const cy = canvas.height / 2;
          ctx.drawImage(img, cx - size / 2, cy - size / 2, size, size);
        }
      }
      
      rafRef.current = requestAnimationFrame(draw);
    };
    draw();

    const start = (e) => { 
      e.preventDefault(); 
      
      // feedback 상태이거나 답변이 이미 선택된 경우 모든 터치/마우스 동작 차단
      if (stagePhase === 'feedback' || stageAnsweredRef.current) {
        return;
      }
      
      // 아이템 선택 체크 (마우스/터치를 눌렀을 때)
      const pos = getCanvasPoint(e);
      if (pos && pos.y >= canvas.height * topUiRatio) {
        const maskCtx = maskCanvasRef.current.getContext('2d');
        const pixel = maskCtx.getImageData(pos.x, pos.y, 1, 1).data;
        if (pixel[3] === 0) { // 커버가 지워진 부분이면
          const clickedItem = items.find(item => Math.hypot(pos.x - item.x, pos.y - item.y) < item.size / 2);
          if (clickedItem) {
            handleSelectAnswer(clickedItem.id);
            return; // 아이템을 선택했으면 지우기 동작 안함
          }
        }
      }
      
      setIsErasing(true); 
      lastPoint.current = getCanvasPoint(e); 
    };
    
    const stop = (e) => { e.preventDefault(); setIsErasing(false); lastPoint.current = null; };
    
    const erase = (e) => {
      if (!isErasing || !maskCanvasRef.current) return;
      
      // feedback 상태이거나 답변이 이미 선택된 경우 지우기 동작 차단
      if (stagePhase === 'feedback' || stageAnsweredRef.current) {
        return;
      }
      
      e.preventDefault();
      const currentPoint = getCanvasPoint(e);
      if (currentPoint && lastPoint.current) {
        const maskCtx = maskCanvasRef.current.getContext('2d');
        maskCtx.globalCompositeOperation = 'destination-out';
        maskCtx.strokeStyle = 'rgba(0,0,0,1)';
        maskCtx.lineWidth = 80;
        maskCtx.lineCap = 'round';
        maskCtx.beginPath();
        maskCtx.moveTo(lastPoint.current.x, lastPoint.current.y);
        maskCtx.lineTo(currentPoint.x, currentPoint.y);
        maskCtx.stroke();
      }
      lastPoint.current = currentPoint;
    };

    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', erase);
    canvas.addEventListener('mouseup', stop);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', erase, { passive: false });
    canvas.addEventListener('touchend', stop);

    return () => {
      canvas.removeEventListener('mousedown', start);
      canvas.removeEventListener('mousemove', erase);
      canvas.removeEventListener('mouseup', stop);
      canvas.removeEventListener('touchstart', start);
      canvas.removeEventListener('touchmove', erase);
      canvas.removeEventListener('touchend', stop);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [stagePhase, imagesReady, items, isErasing, animatingItem, animationStartTime, shakingItem, shakeStartTime, feedbackActive, feedbackIsSuccess, isTutorialStage, includeTutorial]);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
          touchAction: 'none',
          position: 'relative',
          zIndex: 1, // UI 요소들보다 뒤에 위치
        }}
      />
    </div>
  );
};

export default ShortFormQuizEraseAndPick;
